<template>
  <div class="admin-page">
    <div class="container">
      <!-- Page Header -->
      <header class="admin-header">
        <div class="header-title">
          <h1>CMS Publishing Hub</h1>
          <p>Create, manage, and publish news & events to AI Without Border platform.</p>
        </div>
        <button class="back-btn" @click="$router.push('/events')">
          <span>&larr;</span> Back to Events
        </button>
      </header>

      <div class="admin-grid">
        <!-- Form Section -->
        <section class="form-section card">
          <h2>{{ editingId ? 'Edit Event' : 'Create New Event' }}</h2>
          <form @submit.prevent="handleSubmit">
            <!-- Article Title -->
            <div class="form-group">
              <label for="title">Article Title</label>
              <input
                id="title"
                v-model="form.title"
                type="text"
                placeholder="e.g. An Affordable Energy Guarantee"
                required
              />
            </div>

            <!-- Description -->
            <div class="form-group">
              <label for="desc">Short Description (Abstract)</label>
              <textarea
                id="desc"
                v-model="form.desc"
                rows="3"
                placeholder="Describe the article in 1-2 sentences for the listing page..."
                required
              ></textarea>
            </div>

            <!-- Card Image -->
            <div class="form-group">
              <label for="imageUpload">Card Image</label>
              <div class="image-upload-wrapper">
                <input type="file" id="imageUpload" accept="image/*" @change="handleImageUpload" :disabled="uploadingImage" />
                <span v-if="uploadingImage" class="upload-status">Uploading...</span>
                <span v-else-if="form.imageSrc && form.imageSrc !== '/img/event_card_grassland.png'" class="upload-status success">Image ready</span>
              </div>
            </div>

            <!-- Content -->
            <div class="form-group">
              <div class="label-row">
                <label>Article Body (Rich Text / HTML)</label>
                <span class="tip">Paste formatted text or type directly. HTML styles are preserved automatically.</span>
              </div>
              <div
                class="rich-text-editor"
                contenteditable="true"
                @input="handleEditorInput"
                @blur="handleEditorInput"
                ref="editor"
              ></div>
            </div>

            <!-- Action Buttons -->
            <div class="button-group">
              <button v-if="!editingId || form.status === 'draft'" type="button" class="draft-btn" @click="submitForm('draft')" :disabled="submitting">
                <span>Save Draft</span>
              </button>
              <button v-if="editingId && form.status === 'published'" type="button" class="draft-btn" @click="submitForm('draft')" :disabled="submitting">
                <span>Unpublish</span>
              </button>
              <button type="button" class="submit-btn" @click="submitForm('published')" :disabled="submitting">
                <span v-if="submitting" class="spinner"></span>
                <span>{{ submitting ? 'Saving...' : (editingId && form.status === 'published' ? 'Save Changes' : 'Publish Event') }}</span>
              </button>
              <button v-if="editingId" type="button" class="cancel-btn" @click="cancelEdit">
                Cancel
              </button>
            </div>
          </form>
        </section>

        <!-- Preview & List -->
        <div class="sidebar">
          <!-- Card Preview -->
          <section class="preview-section card">
            <h2>Real-time Card Preview</h2>
            <div class="preview-card-wrapper">
              <div class="event-card">
                <div class="event-img" v-if="form.imageSrc || form.title">
                  <img :src="form.imageSrc || '/img/event_card_grassland.png'" alt="Preview Image" />
                </div>
                <div class="event-details">
                  <h3>{{ form.title || 'Your Article Title' }}</h3>
                  <p class="event-date">{{ currentDate }}</p>
                  <p class="event-desc">
                    {{ form.desc || 'Your short description will appear here. This helps users understand what the article is about from the events page.' }}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- Existing List -->
          <section class="list-section card">
            <h2>Existing Articles ({{ articles.length }})</h2>
            <div v-if="loading" class="loading-state">
              <span class="spinner"></span> Loading articles...
            </div>
            <div v-else class="article-list">
              <div v-for="item in articles" :key="item.id" class="article-item">
                <div class="item-info">
                  <h4>{{ item.title }}</h4>
                  <div class="item-meta">
                    <span>{{ item.date }}</span>
                    <span :class="['status-badge', item.status === 'draft' ? 'draft' : 'published']">
                      {{ item.status === 'draft' ? 'Draft' : 'Published' }}
                    </span>
                  </div>
                </div>
                <div class="item-actions">
                  <button class="edit-item-btn" @click="editArticle(item)">
                    Edit
                  </button>
                  <button class="delete-item-btn" @click="deleteArticle(item.id)">
                    Delete
                  </button>
                  <button class="view-item-btn" @click="$router.push('/article/' + item.id)">
                    View
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminPage',
  data() {
    return {
      articles: [],
      loading: false,
      submitting: false,
      uploadingImage: false,
      editingId: null,
      form: {
        title: '',
        desc: '',
        content: '',
        imageSrc: '/img/event_card_grassland.png',
        status: 'published'
      }
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  },
  mounted() {
    this.fetchArticles();
  },
  methods: {
    async fetchArticles() {
      this.loading = true;
      try {
        const response = await axios.get('/api/events?all=true');
        this.articles = response.data;
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        this.loading = false;
      }
    },
    compressImage(file, maxWidth = 1200, quality = 0.7) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                const compressedFile = new File([blob], file.name, {
                  type: file.type || 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              },
              file.type || 'image/jpeg',
              quality
            );
          };
        };
      });
    },
    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploadingImage = true;

      let fileToUpload = file;
      if (file.type && file.type.startsWith('image/')) {
        try {
          fileToUpload = await this.compressImage(file);
        } catch (e) {
          console.error('Failed to compress image:', e);
        }
      }

      const formData = new FormData();
      formData.append('image', fileToUpload);

      try {
        const response = await axios.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        if (response.data.success) {
          this.form.imageSrc = response.data.url;
        }
      } catch (error) {
        console.error('Failed to upload image:', error);
        alert('Failed to upload image.');
      } finally {
        this.uploadingImage = false;
        event.target.value = ''; // Reset input
      }
    },
    handleEditorInput(e) {
      this.form.content = e.target.innerHTML;
    },
    handleSubmit() {
      this.submitForm(this.form.status || 'published');
    },
    async submitForm(status) {
      this.submitting = true;
      this.form.status = status;
      try {
        if (this.editingId) {
          const response = await axios.put(`/api/events/${this.editingId}`, this.form);
          if (response.data.success) {
            alert('Article updated successfully!');
            this.cancelEdit();
            await this.fetchArticles();
          }
        } else {
          const response = await axios.post('/api/events', this.form);
          if (response.data.success) {
            alert('Article saved successfully!');
            // Clear form
            this.form.title = '';
            this.form.desc = '';
            this.form.content = '';
            this.form.imageSrc = '/img/event_card_grassland.png';
            this.form.status = 'published';
            if (this.$refs.editor) {
              this.$refs.editor.innerHTML = '';
            }
            // Refresh list
            await this.fetchArticles();
          }
        }
      } catch (error) {
        console.error('Submission failed:', error);
        alert(error.response?.data?.error || 'Failed to submit article.');
      } finally {
        this.submitting = false;
      }
    },
    editArticle(item) {
      this.editingId = item.id;
      this.form.title = item.title;
      this.form.desc = item.desc;
      this.form.content = item.content;
      this.form.status = item.status || 'published';
      this.form.imageSrc = item.imageSrc || '/img/event_card_grassland.png';

      // Update editor content
      this.$nextTick(() => {
        if (this.$refs.editor) {
          this.$refs.editor.innerHTML = item.content || '';
        }
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.form.title = '';
      this.form.desc = '';
      this.form.content = '';
      this.form.status = 'published';
      this.form.imageSrc = '/img/event_card_grassland.png';
      if (this.$refs.editor) {
        this.$refs.editor.innerHTML = '';
      }
    },
    async deleteArticle(id) {
      if (!confirm('Are you sure you want to delete this article?')) {
        return;
      }
      try {
        const response = await axios.delete(`/api/events/${id}`);
        if (response.data.success) {
          alert('Article deleted successfully!');
          if (this.editingId === id) {
            this.cancelEdit();
          }
          await this.fetchArticles();
        }
      } catch (error) {
        console.error('Failed to delete article:', error);
        alert(error.response?.data?.error || 'Failed to delete article.');
      }
    },
    handleLogout() {
      localStorage.removeItem('isLoggedIn');
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.admin-page {
  background-color: #080915;
  color: #f1f2f6;
  min-height: 100vh;
  padding: 120px 0 60px 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 2rem;
}

.header-title h1 {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #00f2fe 0%, #4facfe 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
}

.header-title p {
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
}

.back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.admin-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2.5rem;
}

.card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
}

.card h2 {
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #fff;
  border-left: 4px solid #00f2fe;
  padding-left: 0.8rem;
}

.form-group {
  margin-bottom: 1.8rem;
}

.form-group label {
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 0.6rem;
  color: rgba(255, 255, 255, 0.85);
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
}

.label-row label {
  margin-bottom: 0;
}

.tip {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.4);
}

input[type="text"],
textarea,
select {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.9rem 1.2rem;
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

input[type="text"]:focus,
textarea:focus,
select:focus {
  border-color: #00f2fe;
  box-shadow: 0 0 10px rgba(0, 242, 254, 0.15);
  background: rgba(255, 255, 255, 0.08);
}

textarea {
  resize: vertical;
}

.rich-text-editor {
  width: 100%;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.9rem 1.2rem;
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.rich-text-editor:empty:before {
  content: "Type or paste your formatted text here...";
  color: rgba(255, 255, 255, 0.4);
  cursor: text;
}

.rich-text-editor:focus {
  border-color: #00f2fe;
  box-shadow: 0 0 10px rgba(0, 242, 254, 0.15);
  background: rgba(255, 255, 255, 0.08);
}

.image-upload-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.image-upload-wrapper input[type="file"] {
  background: rgba(255, 255, 255, 0.05);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  padding: 0.8rem;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  flex: 1;
}

.upload-status {
  font-size: 0.9rem;
  color: #f1c40f;
}

.upload-status.success {
  color: #2ecc71;
}

.submit-btn {
  flex: 2;
  background: linear-gradient(135deg, #00f2fe 0%, #4facfe 100%);
  border: none;
  color: #05060d;
  padding: 1.1rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 242, 254, 0.3);
}

.button-group {
  display: flex;
  gap: 1rem;
  width: 100%;
}

.cancel-btn {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 1.1rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.item-actions {
  display: flex;
  gap: 0.5rem;
}

.edit-item-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.4rem 0.9rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-item-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.4);
}

.delete-item-btn {
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: rgba(239, 68, 68, 0.8);
  padding: 0.4rem 0.9rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.delete-item-btn:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.8);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 242, 254, 0.5);
  filter: brightness(1.1);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

/* Preview Card Styles */
.preview-card-wrapper {
  background: #0b0d1e;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.event-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.event-img img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.event-details {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.event-details h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.4;
  margin: 0;
}

.event-date {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}

.event-desc {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin: 0;
}

/* Existing Article List */
.article-list {
  max-height: 350px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-right: 0.5rem;
}

.article-list::-webkit-scrollbar {
  width: 6px;
}

.article-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.article-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.article-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(0, 242, 254, 0.3);
}

.item-info h4 {
  font-size: 0.95rem;
  font-weight: 500;
  color: #fff;
  margin: 0 0 0.3rem 0;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-info span {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.4);
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.status-badge {
  font-size: 0.75rem !important;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
}

.status-badge.published {
  background: rgba(46, 204, 113, 0.15);
  color: #2ecc71 !important;
  border: 1px solid rgba(46, 204, 113, 0.3);
}

.status-badge.draft {
  background: rgba(241, 196, 15, 0.15);
  color: #f1c40f !important;
  border: 1px solid rgba(241, 196, 15, 0.3);
}

.draft-btn {
  flex: 1;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 1.1rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.draft-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}

.view-item-btn {
  background: transparent;
  border: 1px solid rgba(0, 242, 254, 0.4);
  color: #00f2fe;
  padding: 0.4rem 0.9rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-item-btn:hover {
  background: rgba(0, 242, 254, 0.1);
  border-color: #00f2fe;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  justify-content: center;
  padding: 2rem 0;
}

/* Spinner Animation */
.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1024px) {
  .admin-grid {
    grid-template-columns: 1fr;
  }
}
</style>
